import { readAudioSeconds, readVideoSeconds } from './articleDuration';
import { paintMediaButtonAvailability } from './articleNavVisibility';
import type { ChapterApiResponse } from './articleControllerTypes';
import { byId } from './domUtils';
import { clearStaleInertUnderRoot } from './modalAccessibility';
import { attachAudioPlayer, attachVideoPlayer } from './mediaPlayer';
import { wireMediaEndedCommands } from './mediaPlayerEndedControls';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import type { ChapterPageModel } from '../../shared/pages/pageModels';

/** Connects a chapter response to the reusable audio/video player runtime. */
export function wireArticleMedia(root: Root, response: ChapterApiResponse): void {
	// Defensive inert reset: when a chapter paints, the article view must be
	// interactive so the Listen and Watch capsules (and any other article
	// controls) actually receive click events. If a prior modal or dock flow
	// left `.app-layout` or `#view-article` inert, the user taps the pills
	// and nothing happens. Clearing stale inert from the article entry point
	// is cheaper than reproducing the exact prior modal that leaked it.
	clearStaleInertUnderRoot(root);
	const runtime = getRuntime(root);
	const topicId = runtime?.activeCourseId;
	const chapterIndex = runtime?.activeChapterIndex;
	const audioSeconds = readAudioSeconds(response);
	const videoSeconds = readVideoSeconds(response);
	// The Listen pill is located by id: its proto-click is the mobile five-key
	// transport's playArticleAudio() (prototype round-4), so an attribute
	// selector keyed to the old showPlayer() token would miss it entirely.
	const audioButton = byId(root, 'articleAudioBtn');
	const videoButton = byId(root, 'articleVideoBtn');

	if (typeof response.chapter?.index === 'number') {
		audioButton?.setAttribute('data-chapter-index', String(response.chapter.index));
		videoButton?.setAttribute('data-chapter-index', String(response.chapter.index));
	}
	if (runtime && topicId && response.chapter?.id && typeof response.chapter.index === 'number') {
		runtime.chapterIdByIndex = {
			...(runtime.chapterIdByIndex ?? {}),
			[topicId]: { ...(runtime.chapterIdByIndex?.[topicId] ?? {}), [response.chapter.index]: response.chapter.id },
		};
	}
	if (runtime && topicId && typeof chapterIndex === 'number' && audioSeconds > 0) {
		runtime.audioSecondsByChapter = {
			...(runtime.audioSecondsByChapter ?? {}),
			[`${topicId}:${chapterIndex}`]: audioSeconds,
		};
	}
	if (runtime && topicId && typeof chapterIndex === 'number' && videoSeconds > 0) {
		runtime.videoSecondsByChapter = {
			...(runtime.videoSecondsByChapter ?? {}),
			[`${topicId}:${chapterIndex}`]: videoSeconds,
		};
	}

	const canPlayAudio = response.access?.media.audio === 'allow';
	const canPlayVideo = response.access?.media.video === 'allow';
	if ((canPlayAudio || canPlayVideo) && topicId && typeof chapterIndex === 'number' && runtime?.api) {
		attachAudioPlayer(root, runtime.api, topicId, chapterIndex, runtime.currentPlan ?? 'free');
		attachVideoPlayer(root, runtime.api, topicId, chapterIndex, runtime.currentPlan ?? 'free');
		wireMediaEndedCommands(root, runtime.api, runtime.currentPlan ?? 'free');
	}
	paintMediaButtonAvailability(audioButton, Boolean(response.chapter?.hasAudio));
	paintMediaButtonAvailability(videoButton, Boolean(response.chapter?.hasVideo));
}

/** Attaches media controls for a preserved server-rendered Chapter without repainting its content. */
export function wireSsrArticleMedia(root: Root, model: ChapterPageModel): void {
	const mediaAccess = {
		audio: model.access.canUseAudio ? 'allow' : model.access.authenticated ? 'paywall' : 'login',
		video: model.access.canUseVideo ? 'allow' : model.access.authenticated ? 'paywall' : 'login',
	} as const;
	wireArticleMedia(root, {
		audioSeconds: model.chapter.audioSeconds,
		chapter: model.chapter,
		access: {
			canOpen: true,
			text: { mode: model.chapter.bodyMode === 'full' ? 'full' : 'partial', visibleRatio: model.access.visibleRatio, gate: model.access.gate },
			media: mediaAccess,
			requiredPlan: model.access.requiredPlan,
		},
	});
}
